import { Dispatch, SetStateAction, useTransition } from "react"
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay"
import { patientTableContainerCHQuery } from "@/modules/patients/patient-list/__generated__/patientTableContainerCHQuery.graphql"
import { patientTableContainerCHSearchableQuery$key } from "@/modules/patients/patient-list/__generated__/patientTableContainerCHSearchableQuery.graphql"
import { patientTableContainerDEQuery } from "@/modules/patients/patient-list/__generated__/patientTableContainerDEQuery.graphql"
import { patientTableContainerDESearchableQuery$key } from "@/modules/patients/patient-list/__generated__/patientTableContainerDESearchableQuery.graphql"
import { PATIENT_PAGE_SIZE } from "@/modules/patients/patient-list/config"
import { PatientSearchableTable } from "@/modules/patients/patient-list/patient-searchable-table"
import { Country } from "@/types/__generated__/union-types"

type PatientTableContainerProps = {
  country: Country
  keyword: string
  isHealthInsuranceCardNumberMissing?: boolean | null
  isPotentialDuplicate?: boolean | null
  phoneNumber?: string
  hasFilterChanged: boolean
  setPatientQuery: Dispatch<SetStateAction<string>>
}

const chPatientSearchQuery = graphql`
  query patientTableContainerCHQuery($patientSearchInput: PatientSearchInput!, $first: Int) {
    ...patientTableContainerCHSearchableQuery @arguments(patientSearchInput: $patientSearchInput, first: $first)
  }
`

const dePatientSearchQuery = graphql`
  query patientTableContainerDEQuery($patientSearchInput: PatientSearchInput!, $first: Int) {
    ...patientTableContainerDESearchableQuery @arguments(patientSearchInput: $patientSearchInput, first: $first)
  }
`

const chPatientSearchFragment = graphql`
  fragment patientTableContainerCHSearchableQuery on Query
  @argumentDefinitions(
    first: { type: "Int" }
    after: { type: "String" }
    patientSearchInput: { type: "PatientSearchInput!" }
  )
  @refetchable(queryName: "ChPatientSearchPaginationQuery") {
    patients: ch_patientSearch(first: $first, after: $after, patientSearchInput: $patientSearchInput)
      @connection(key: "ChPatientSearchFragment_patients") {
      edges {
        node {
          ...patientSearchableTable @relay(mask: false)
        }
      }
      totalCount
    }
  }
`

const dePatientSearchFragment = graphql`
  fragment patientTableContainerDESearchableQuery on Query
  @argumentDefinitions(
    first: { type: "Int" }
    after: { type: "String" }
    patientSearchInput: { type: "PatientSearchInput!" }
  )
  @refetchable(queryName: "DePatientSearchPaginationQuery") {
    patients: de_patientSearch(first: $first, after: $after, patientSearchInput: $patientSearchInput)
      @connection(key: "DePatientSearchFragment_patients") {
      edges {
        node {
          ...patientSearchableTable @relay(mask: false)
        }
      }
      totalCount
    }
  }
`

const PatientTableContainer = ({
  country,
  keyword,
  isHealthInsuranceCardNumberMissing,
  isPotentialDuplicate,
  phoneNumber,
  hasFilterChanged,
  setPatientQuery,
}: PatientTableContainerProps) => {
  const [, startTransition] = useTransition()

  const descriptor =
    country === "CH"
      ? { query: chPatientSearchQuery, fragment: chPatientSearchFragment }
      : { query: dePatientSearchQuery, fragment: dePatientSearchFragment }

  const queryRef = useLazyLoadQuery<patientTableContainerCHQuery | patientTableContainerDEQuery>(
    descriptor.query,
    {
      patientSearchInput: {
        searchText: keyword,
        isHealthInsuranceCardMissing: isHealthInsuranceCardNumberMissing || null,
        isPotentialDuplicate: isPotentialDuplicate || null,
        orderByMarkedAsPotentialDuplicateAt: isPotentialDuplicate || null,
        phoneNumber,
      },
      first: PATIENT_PAGE_SIZE,
    },
    { fetchPolicy: "network-only" },
  )

  const pagingKey: patientTableContainerCHSearchableQuery$key | patientTableContainerDESearchableQuery$key = queryRef

  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment(descriptor.fragment, pagingKey)
  const patients = data.patients?.edges?.map((edge) => edge.node) ?? []

  const loadMore = () => {
    if (hasNext && !isLoadingNext) {
      loadNext(PATIENT_PAGE_SIZE)
    }
  }

  const handlePatientUpdated = () => {
    startTransition(() => {
      refetch({}, { fetchPolicy: "network-only" })
    })
  }

  return (
    <PatientSearchableTable
      keyword={keyword}
      data={patients}
      hasFilterChanged={hasFilterChanged}
      isHealthInsuranceCardNumberMissing={!!isHealthInsuranceCardNumberMissing}
      endReached={loadMore}
      totalCount={data.patients?.totalCount ?? 0}
      setPatientQuery={setPatientQuery}
      onPatientUpdated={handlePatientUpdated}
    />
  )
}

export { PatientTableContainer }
